import { windOffsetPx } from '../environment/wind.ts';
import type { MapDocument } from '../map/types.ts';
import { add, dot, fromAngle, headingUnit, sub } from '../math.ts';
import { bowPosition, projectMovementArea } from '../movement.ts';
import { paceFeel, ringProjection, type TurnFeel } from '../turnFeel.ts';
import type { BoatState, Vec2 } from '../types.ts';

/** Fraction of the reach each ring sits at. Just inside the rim so float error stays legal. */
const RING_FRACTIONS = [0.98, 0.75, 0.5, 0.25] as const;
/** Share of the density each ring gets, outermost first: the rim is where speed changes most. */
const RING_WEIGHTS = [0.4, 0.3, 0.2, 0.1] as const;

function reachArea(boat: BoatState, map: MapDocument, feel: TurnFeel) {
  const paced = paceFeel(boat, feel);
  const view = ringProjection(boat.speed, paced);
  const wind = windOffsetPx(map.environment, boat.speed, paced.windFeel);
  return { view, area: projectMovementArea(boat, view.diskRadius, wind, view.projected.coastPx) };
}

/** Where the boat drifts with no input: the centre of this turn's reach. */
export function coastPoint(boat: BoatState, map: MapDocument, feel: TurnFeel): Vec2 {
  return reachArea(boat, map, feel).area.center;
}

/**
 * Destinations to try this turn: rings through the (possibly oval) reach around the coast point,
 * angle 0 dead ahead, plus the coast point itself. Points behind the bow are dropped here, as
 * `isValidDestination` would; land is left to `planMove`, which the search calls anyway.
 */
export function candidateDestinations(
  boat: BoatState,
  map: MapDocument,
  feel: TurnFeel,
  density: number,
): Vec2[] {
  const { view, area } = reachArea(boat, map, feel);
  const axes = view.reach ?? {
    turn: view.diskRadius,
    accel: view.diskRadius,
    brake: view.diskRadius,
  };
  const total = Math.max(4, Math.round(density));
  const bow = bowPosition(boat, feel.bowOffsetPx);
  const ahead = headingUnit(boat.heading);
  const points: Vec2[] = [area.center];

  RING_FRACTIONS.forEach((fraction, ring) => {
    const count = Math.max(4, Math.round(total * (RING_WEIGHTS[ring] ?? 0)));
    // Offset alternate rings by half a step so the samples do not line up on the same spokes.
    const phase = ring % 2 === 0 ? 0 : Math.PI / count;

    for (let index = 0; index < count; index += 1) {
      const angle = phase + (index / count) * Math.PI * 2;
      const along = Math.cos(angle);
      const across = Math.sin(angle);
      const axial = along >= 0 ? axes.accel : axes.brake;
      const forward = fromAngle(boat.heading, along * axial * fraction);
      const side = fromAngle(boat.heading + Math.PI / 2, across * axes.turn * fraction);
      points.push(add(area.center, add(forward, side)));
    }
  });

  return points.filter((point) => dot(sub(point, bow), ahead) > 0);
}
